import { dishArchived, dishSoldOut } from "../constants";
import type { CartItem } from "../store/cart/cart.types";
import type { DishPromo } from "./promoCheck";
import {
  formatPromoAvailabilityText,
  isDishPromoUnavailableAt,
  isDishVisibleWithPromoAt,
  normalizeDishPromo,
} from "./promoCheck";

export type CartAvailabilityOptions = {
  /** Takeaway scheduled pickup — promo lines are checked at this time, not now. */
  scheduledPickupAt?: Date | null;
  /** Venue IANA timezone used to evaluate promo day-parts. */
  timezone?: string;
};

type KitchenLike = {
  dishes?: Record<string, unknown> | unknown;
  timezone?: string;
};

const getDishesRecord = (kitchen: KitchenLike | null | undefined) => {
  const raw = kitchen?.dishes;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return {} as Record<string, unknown>;
};

export type LiveMenuDish = Record<string, unknown> & {
  id: string;
  dishStatus?: string;
  isDishAvailableOnline?: boolean;
  promo?: unknown;
  stockDetails?: { isEnabled?: boolean; quantity?: number | null };
};

export const getLiveDishFromKitchen = (
  kitchen: KitchenLike | null | undefined,
  dishId: string | undefined
): LiveMenuDish | null => {
  if (!dishId) return null;
  const dishes = getDishesRecord(kitchen);
  const live = dishes[dishId];
  if (!live || typeof live !== "object") return null;
  return { ...(live as object), id: dishId } as LiveMenuDish;
};

const asPromoDish = (live: LiveMenuDish | null) =>
  live as { promo?: DishPromo | null };

/**
 * Human-readable reason this dish cannot be ordered (or null if orderable).
 * Priority: removed → archived → offline → sold out / stock → promo window.
 */
export const getUnavailabilityMessageForLiveDish = (
  live: LiveMenuDish | null,
  options?: CartAvailabilityOptions
): string | null => {
  const promoAt = options?.scheduledPickupAt ?? null;
  const isScheduledPromoCheck = promoAt != null;
  if (!live) {
    return "Item no longer available. Remove to continue";
  }
  if (live.dishStatus === dishArchived) {
    return "Item no longer available. Remove to continue";
  }
  if (live.isDishAvailableOnline === false) {
    return "Item no longer available. Remove to continue";
  }
  if (live.dishStatus === dishSoldOut) {
    return "Item is sold out. Remove to continue.";
  }
  if (
    live.stockDetails?.isEnabled &&
    Number(live.stockDetails.quantity) === 0
  ) {
    return "Item is sold out. Remove to continue.";
  }

  const promoDish = asPromoDish(live);
  const normalizedPromo = normalizeDishPromo(promoDish.promo ?? null);
  if (normalizedPromo) {
    const timezone = options?.timezone;
    const at = isScheduledPromoCheck ? promoAt : undefined;
    const outsideHideWindow = !isDishVisibleWithPromoAt(promoDish, at, timezone);
    const outsideSoftWindow = isDishPromoUnavailableAt(promoDish, at, timezone);
    if (outsideHideWindow || outsideSoftWindow) {
      const slot = formatPromoAvailabilityText(promoDish.promo ?? null);
      if (slot) {
        const head = slot.replace(/\.$/, "");
        if (isScheduledPromoCheck) {
          return `${head}. Update time or remove to continue.`;
        }
        return `${head}. Remove to continue.`;
      }
    }
  }

  return null;
};

export const isLiveDishOrderable = (
  live: LiveMenuDish | null
): boolean => getUnavailabilityMessageForLiveDish(live) === null;

/**
 * Map of `cartItemId` → specific message (promo, sold out, removed, etc.).
 */
export const getCartLineAvailabilityMessages = (
  kitchen: KitchenLike | null | undefined,
  cartItems: CartItem[] | null | undefined,
  options?: CartAvailabilityOptions
): Map<string, string> => {
  const map = new Map<string, string>();
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return map;
  }

  const dishes = getDishesRecord(kitchen);
  if (!kitchen || Object.keys(dishes).length === 0) {
    return map;
  }

  const resolvedOptions: CartAvailabilityOptions = {
    ...options,
    timezone: options?.timezone ?? kitchen?.timezone,
  };

  const addLine = (line: CartItem) => {
    const live = getLiveDishFromKitchen(kitchen, line.id);
    const msg = getUnavailabilityMessageForLiveDish(live, resolvedOptions);
    if (msg) {
      map.set(line.cartItemId, msg);
    }
  };

  cartItems.forEach((item) => {
    if (item.bundleParentId) {
      addLine(item);
      return;
    }

    if (item.isBundle === true) {
      addLine(item);
      cartItems.forEach((child) => {
        if (child.bundleParentId === item.cartItemId) {
          addLine(child);
        }
      });
      return;
    }

    addLine(item);
  });

  return map;
};

export const getInvalidCartLineIds = (
  kitchen: KitchenLike | null | undefined,
  cartItems: CartItem[] | null | undefined
): Set<string> => new Set(getCartLineAvailabilityMessages(kitchen, cartItems).keys());

export const hasInvalidCartLines = (
  kitchen: KitchenLike | null | undefined,
  cartItems: CartItem[] | null | undefined
): boolean => getCartLineAvailabilityMessages(kitchen, cartItems).size > 0;
